import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FileText, Clock, AlertTriangle, CheckCircle,
  TrendingUp, Calendar, ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { adminApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const statusLabels = {
  'RECEBIDA': 'Recebida',
  'EM_TRIAGEM': 'Em Triagem',
  'EM_INVESTIGACAO': 'Em Investigação',
  'AGUARDA_INFO': 'Aguarda Info',
  'CONCLUIDA': 'Concluída',
  'ARQUIVADA': 'Arquivada'
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentReports, setRecentReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const isCaseManager = user?.role === 'case_manager';

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Case managers see THEIR cases sorted by feedback deadline
        // (most urgent first). Everyone else keeps chronological order.
        const listParams = isCaseManager
          ? { limit: 10, sort: 'deadline' }
          : { limit: 5 };
        const [statsRes, reportsRes] = await Promise.all([
          adminApi.getDashboard(),
          adminApi.getReports(listParams),
        ]);
        setStats(statsRes.data);
        let list = reportsRes.data.reports || [];
        if (isCaseManager) {
          // Client-side fallback sort (backend may or may not honor `sort`).
          list = [...list].sort((a, b) => {
            const da = a.feedback_deadline || '9999-12-31';
            const db = b.feedback_deadline || '9999-12-31';
            return da.localeCompare(db);
          });
        }
        setRecentReports(list);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isCaseManager]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">A carregar...</div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          Dashboard
        </h1>
        <p className="text-slate-500 mt-1">Visão geral do canal de denúncias</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="card-hover cursor-pointer" onClick={() => navigate('/admin/denuncias')} data-testid="card-total-reports">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Denúncias</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="total-reports">
                  {stats?.total_reports || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover cursor-pointer" onClick={() => navigate('/admin/denuncias')} data-testid="card-this-month">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Este Mês</p>
                <p className="text-3xl font-bold text-slate-900">
                  {stats?.reports_this_month || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-sky-100 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-sky-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`card-hover cursor-pointer ${stats?.overdue_acknowledgments > 0 ? 'border-red-200 bg-red-50/50' : ''}`} onClick={() => navigate('/admin/denuncias?overdue=ack')} data-testid="card-overdue-ack">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Confirmações Vencidas (D+7)</p>
                <p className={`text-3xl font-bold ${stats?.overdue_acknowledgments > 0 ? 'text-red-600' : 'text-slate-900'}`} data-testid="overdue-ack">
                  {stats?.overdue_acknowledgments || 0}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${stats?.overdue_acknowledgments > 0 ? 'bg-red-100' : 'bg-emerald-100'}`}>
                {stats?.overdue_acknowledgments > 0 ? (
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                ) : (
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`card-hover cursor-pointer ${stats?.overdue_feedbacks > 0 ? 'border-amber-200 bg-amber-50/50' : ''}`} onClick={() => navigate('/admin/denuncias?overdue=feedback')} data-testid="card-overdue-feedback">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Feedbacks Vencidos (D+90)</p>
                <p className={`text-3xl font-bold ${stats?.overdue_feedbacks > 0 ? 'text-amber-600' : 'text-slate-900'}`} data-testid="overdue-feedback">
                  {stats?.overdue_feedbacks || 0}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${stats?.overdue_feedbacks > 0 ? 'bg-amber-100' : 'bg-emerald-100'}`}>
                {stats?.overdue_feedbacks > 0 ? (
                  <Clock className="w-6 h-6 text-amber-600" />
                ) : (
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Denúncias por Estado</CardTitle>
            <CardDescription>Distribuição atual das denúncias</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats?.reports_by_status || {}).map(([status, count]) => (
                <Link
                  key={status}
                  to={`/admin/denuncias?status=${status}`}
                  className="flex items-center justify-between rounded-md px-2 py-1 -mx-2 hover:bg-slate-50 transition-colors"
                  data-testid={`status-row-${status}`}
                >
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`status-${status.toLowerCase()}`}>
                      {statusLabels[status] || status}
                    </Badge>
                  </div>
                  <span className="font-medium text-slate-900">{count}</span>
                </Link>
              ))}
              {Object.keys(stats?.reports_by_status || {}).length === 0 && (
                <p className="text-center text-slate-400 py-4">Sem dados</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Compliance Clock</CardTitle>
            <CardDescription>Prazos legais (Lei 93/2021)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => navigate('/admin/denuncias?overdue=ack')} data-testid="clock-ack">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Confirmação de Receção (D+7)</span>
                  <Badge className={stats?.overdue_acknowledgments > 0 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}>
                    {stats?.overdue_acknowledgments > 0 ? `${stats.overdue_acknowledgments} vencidos` : 'Em dia'}
                  </Badge>
                </div>
                <p className="text-xs text-slate-500">
                  Prazo máximo de 7 dias úteis para enviar confirmação ao denunciante
                </p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => navigate('/admin/denuncias?overdue=feedback')} data-testid="clock-feedback">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Feedback (D+90)</span>
                  <Badge className={stats?.overdue_feedbacks > 0 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>
                    {stats?.overdue_feedbacks > 0 ? `${stats.overdue_feedbacks} vencidos` : 'Em dia'}
                  </Badge>
                </div>
                <p className="text-xs text-slate-500">
                  Prazo máximo de 3 meses para comunicar seguimento ao denunciante
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Reports / My Cases */}
      <Card data-testid={isCaseManager ? 'my-cases-card' : 'recent-reports-card'}>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>{isCaseManager ? 'Os Meus Casos' : 'Denúncias Recentes'}</CardTitle>
            <CardDescription>
              {isCaseManager
                ? 'Casos atribuídos a si ordenados pelo prazo mais urgente'
                : 'Últimas 5 denúncias recebidas'}
            </CardDescription>
          </div>
          <Link to="/admin/denuncias">
            <Button variant="outline" size="sm">
              Ver Todas
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentReports.length > 0 ? (
              recentReports.map((report) => {
                const now = new Date();
                const deadline = report.feedback_deadline ? new Date(report.feedback_deadline) : null;
                const daysLeft = deadline ? Math.round((deadline - now) / 86400000) : null;
                const urgent = daysLeft !== null && daysLeft <= 7;
                return (
                  <Link
                    key={report.id}
                    to={`/admin/denuncias/${report.id}`}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    data-testid={`case-row-${report.tracking_code}`}
                  >
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="font-mono text-sm font-medium text-slate-900">
                          {report.tracking_code}
                        </p>
                        <p className="text-sm text-slate-500">{report.category}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {isCaseManager && daysLeft !== null && (
                        <Badge
                          className={urgent ? 'bg-red-100 text-red-700 border-red-200' : 'bg-slate-100 text-slate-600 border-slate-200'}
                          variant="outline"
                        >
                          {daysLeft < 0 ? `${Math.abs(daysLeft)} dias em atraso` : `${daysLeft} dias`}
                        </Badge>
                      )}
                      <Badge variant="outline" className={`status-${report.status.toLowerCase()}`}>
                        {statusLabels[report.status] || report.status}
                      </Badge>
                      <span className="text-sm text-slate-400">
                        {formatDate(report.created_at)}
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </Link>
                );
              })
            ) : (
              <p className="text-center text-slate-400 py-8">
                {isCaseManager ? 'Ainda não tem casos atribuídos' : 'Ainda não há denúncias'}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
